import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { Depot, DepotSummary, Money, Order, TradeIdeaDetail, Transaction }
  from '../api/client'
import { Dialog } from '../components/common/Dialog'
import { Input, Select } from '../components/common/Controls'
import { geldtext, zeitpunkt } from './priceFormat'
import { SIDE, STATUS, stueckzahl } from './tradeIdeaVocabulary'
import {
  dezimal, fehlertext, inTagen, jetztLokal, leerOderNichtNegativ,
  nichtNegativ, positiv,
} from './formHelpers'
import s from './DepotDialogs.module.scss'

/**
 * Das Schreiben im Depot — Orders, Ausführungen, Transaktionen.
 *
 * DER AGENT ORDERT, DER MENSCH BUCHT. Eine Order ist eine Absicht und darf
 * vom Agenten kommen (Runde zwei, über den MCP-Zugang) oder von hier. Was
 * der Broker daraus gemacht hat, weiß nur, wer beim Broker war — deshalb
 * bucht Ausführungen und Transaktionen allein der Mensch am Schirm.
 */

type Seite = 'BUY' | 'SELL'

/** Was die Dialoge von einer Idee brauchen. `TradeIdeaDetail` erfüllt es. */
export type IdeeKurz = Pick<TradeIdeaDetail,
  'id' | 'name' | 'currency' | 'exchange' | 'status' | 'position' | 'tranches'>

/**
 * Eine Position im Depot, aus der heraus gehandelt wird — Verkaufen oder
 * Nachkaufen direkt aus dem Bestand. Titel und Platz stehen fest; die Idee
 * dahinter, wenn es eine gibt, wird mitgebucht.
 */
export interface Bestand {
  instrument_id: number
  isin: string
  name: string
  exchange: string
  idea_id: number | null
  /** Was da ist, als Vorbelegung beim Verkauf. */
  quantity: string
  average_price: Money | null
  seite: Seite
}

interface Rueckmeldung {
  onFertig: () => void
  onAbbrechen: () => void
}

/** Ein Vorgang gegen den Server: läuft, Fehler, Auslöser. */
function useVorgang(onFertig: () => void) {
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  function starten(aufruf: () => Promise<unknown>) {
    setLaeuft(true)
    setFehler(null)
    aufruf()
      .then(onFertig)
      .catch((cause) => { setFehler(fehlertext(cause)); setLaeuft(false) })
  }
  return { laeuft, fehler, starten }
}

//: Das zuletzt benutzte Depot, je Browser. Wer drei Depots hat und immer
//: im selben handelt, soll es nicht bei jeder Order neu wählen müssen.
const LETZTES_DEPOT = 'iw.depot'

function letztesDepot(): number | null {
  try {
    const wert = localStorage.getItem(LETZTES_DEPOT)
    return wert ? Number(wert) : null
  } catch { return null }
}

function merkeDepot(id: number) {
  try { localStorage.setItem(LETZTES_DEPOT, String(id)) } catch { /* egal */ }
}

/**
 * Die Depots zur Auswahl — geladen, sobald ein Dialog sie braucht.
 *
 * Vorbelegt mit dem zuletzt benutzten, sonst mit dem ersten. Ein einzelnes
 * Depot wird trotzdem gezeigt: Dann weiß man, wohin die Order geht.
 */
function useDepotWahl(vorgabe?: number) {
  const [depots, setDepots] = useState<DepotSummary[] | null>(null)
  const [depotId, setDepotId] = useState<number | null>(vorgabe ?? null)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    let lebendig = true
    api.depots()
      .then((liste) => {
        if (!lebendig) return
        setDepots(liste)
        setDepotId((bisher) => {
          if (bisher && liste.some((d) => d.id === bisher)) return bisher
          const letztes = letztesDepot()
          if (letztes && liste.some((d) => d.id === letztes)) return letztes
          return liste[0]?.id ?? null
        })
      })
      .catch((cause) => { if (lebendig) setFehler(fehlertext(cause)) })
    return () => { lebendig = false }
  }, [])

  const depot = depots?.find((d) => d.id === depotId) ?? null
  return { depots, depot, depotId, setDepotId, fehler }
}

function DepotWahl({ wahl }: { wahl: ReturnType<typeof useDepotWahl> }) {
  const { depots, depotId, setDepotId } = wahl
  return (
    <Select label="Depot" value={depotId ?? ''}
            hint={wahl.depot
              ? `Frei: ${geldtext(wahl.depot.kasse.free)}`
              : depots?.length === 0 ? 'Noch kein Depot angelegt.' : undefined}
            onChange={(event) => setDepotId(Number(event.target.value))}>
      {depots === null && <option value="">lädt …</option>}
      {depots?.map((d) => (
        <option key={d.id} value={d.id}>{d.name} · {d.currency}</option>
      ))}
    </Select>
  )
}

function TrancheWahl({ idee, wert, setzen }: {
  idee: IdeeKurz; wert: string; setzen: (v: string) => void
}) {
  return (
    <Select label="Tranche" value={wert}
            hint="Welche Stufe des Plans der Kauf erfüllt. Leer bei einem Kauf außerhalb des Plans."
            onChange={(event) => setzen(event.target.value)}>
      <option value="">keine</option>
      {idee.tranches.map((t) => (
        <option key={t.sequence} value={t.sequence}>
          {t.sequence}{t.note ? ` — ${t.note}` : ''}
        </option>
      ))}
    </Select>
  )
}

// ================================================================= Orders

/**
 * Eine Limitorder anlegen — auf eine Idee, die gilt.
 *
 * Verkaufen lässt sich nur, was im Markt ist: Die Seite SELL erscheint
 * erst, wenn Stücke da sind. Der Server prüft die Deckung noch einmal und
 * nennt beide Zahlen, wenn sie nicht reicht.
 */
export function OrderDialog({ idee, depotId, onFertig, onAbbrechen }: {
  idee: IdeeKurz
  /** Vorbelegtes Depot, etwa von der Depotseite aus. */
  depotId?: number
} & Rueckmeldung) {
  const wahl = useDepotWahl(depotId)
  const [seite, setSeite] = useState<Seite>('BUY')
  const [stueck, setStueck] = useState('')
  const [limit, setLimit] = useState('')
  const [bis, setBis] = useState(inTagen(14))
  const [tranche, setTranche] = useState('')
  const [grund, setGrund] = useState('')
  const { laeuft, fehler, starten } = useVorgang(onFertig)

  // PROPOSED ist noch keine Entscheidung. Der Server weist es ohnehin ab;
  // der Hinweis sagt es vorher.
  const orderbar = idee.status === 'ACTIVE' || idee.status === 'ENTERED'
  const vollstaendig = orderbar && wahl.depotId !== null && positiv(stueck)
                    && positiv(limit) && bis !== '' && grund.trim().length >= 3

  function anlegen() {
    const depot = wahl.depotId!
    merkeDepot(depot)
    starten(() => api.orderPlace(depot, {
      idea_id: idee.id, side: seite, quantity: dezimal(stueck),
      limit_price: dezimal(limit), valid_until: bis, reason: grund.trim(),
      tranche_sequence: seite === 'BUY' && tranche ? Number(tranche) : null,
    }))
  }

  return (
    <Dialog titel={`Order anlegen — ${idee.name}`} bestaetigen="Anlegen"
            gesperrt={!vollstaendig} laeuft={laeuft}
            fehler={fehler ?? wahl.fehler}
            onBestaetigen={anlegen} onAbbrechen={onAbbrechen}>
      <p className={s.hinweis}>
        Eine Limitorder für {idee.name} an {idee.exchange}. Das Limit steht
        in der Währung des Depots{wahl.depot ? ` (${wahl.depot.currency})` : ''},
        nicht in der des Handelsplatzes. Sie bindet Geld oder Stücke, bis Du
        ihre Ausführung im Depot buchst oder sie stornierst.
      </p>
      {!orderbar && (
        <p className={s.lage}>
          Die Idee steht auf „{STATUS[idee.status] ?? idee.status}". Geordert
          wird erst, wenn sie gilt — mit einer Fortschreibung auf „gilt".
        </p>
      )}
      {idee.position.open && (
        <p className={s.lage}>
          Im Markt: {stueckzahl(idee.position.quantity)} Stück
          {idee.position.average_price
            ? ` zu Ø ${geldtext(idee.position.average_price)}` : ''}.
        </p>
      )}
      <div className={s.felder}>
        <div className={s.breit}><DepotWahl wahl={wahl} /></div>
        <Select label="Seite" value={seite}
                onChange={(event) => setSeite(event.target.value as Seite)}>
          <option value="BUY">{SIDE.BUY}</option>
          {idee.position.open && <option value="SELL">{SIDE.SELL}</option>}
        </Select>
        <Input label="Stück" inputMode="decimal" value={stueck} autoFocus
               placeholder="100"
               onChange={(event) => setStueck(event.target.value)} />
        <Input label={`Limit in ${wahl.depot?.currency ?? 'Depotwährung'}`}
               inputMode="decimal" value={limit} placeholder="41,80"
               onChange={(event) => setLimit(event.target.value)} />
        <Input label="Gültig bis" type="date" value={bis} min={inTagen(0)}
               onChange={(event) => setBis(event.target.value)} />
        {seite === 'BUY' && (
          <TrancheWahl idee={idee} wert={tranche} setzen={setTranche} />
        )}
        <div className={s.breit}>
          <Input label="Begründung" value={grund}
                 placeholder="Warum dieser Titel, warum jetzt"
                 onChange={(event) => setGrund(event.target.value)} />
        </div>
      </div>
    </Dialog>
  )
}

/**
 * Eine Order als ausgeführt buchen.
 *
 * Kurs und Stück sind mit der Order vorbelegt, die Gebühr mit der des
 * Depots. Weniger Stück ist erlaubt — der Rest verfällt mit der Order.
 */
export function AusfuehrungDialog({ order, gebuehr, onFertig, onAbbrechen }: {
  order: Order
  /** Die Gebühr des Depots, zum Vorbelegen — wenn die Seite sie kennt. */
  gebuehr?: Money | null
} & Rueckmeldung) {
  const [kurs, setKurs] = useState(order.limit_price.amount)
  const [stueck, setStueck] = useState(order.quantity)
  const [wann, setWann] = useState(jetztLokal())
  const [gebuehrText, setGebuehr] = useState(gebuehr?.amount ?? '')
  const [notiz, setNotiz] = useState('')
  const { laeuft, fehler, starten } = useVorgang(onFertig)

  const vollstaendig = positiv(kurs) && positiv(stueck) && wann !== ''
                    && leerOderNichtNegativ(gebuehrText)

  function buchen() {
    starten(() => api.orderExecute(order.id, {
      price: dezimal(kurs),
      quantity: dezimal(stueck),
      // Das Feld liefert lokale Zeit ohne Zone; der Server will einen
      // Zeitpunkt. `new Date` deutet den Wert in der Zone des Browsers, und
      // die ist die, in der gehandelt wurde.
      executed_at: new Date(wann).toISOString(),
      fee: gebuehrText.trim() ? dezimal(gebuehrText) : null,
      note: notiz.trim() || null,
    }))
  }

  return (
    <Dialog titel={`Ausgeführt — ${order.name}`} bestaetigen="Buchen"
            gesperrt={!vollstaendig} laeuft={laeuft} fehler={fehler}
            onBestaetigen={buchen} onAbbrechen={onAbbrechen}>
      <p className={s.hinweis}>
        Was der Broker ausgeführt hat: {SIDE[order.side]}{' '}
        {stueckzahl(order.quantity)} Stück, Limit {geldtext(order.limit_price)},
        im Depot „{order.depot_name}". Weniger Stück ist erlaubt, der Rest
        verfällt mit der Order.
      </p>
      <div className={s.felder}>
        <Input label={`Kurs in ${order.currency}`} inputMode="decimal"
               value={kurs} autoFocus
               hint="Die Währung des Depots, wie das Limit."
               onChange={(event) => setKurs(event.target.value)} />
        <Input label="Stück" inputMode="decimal" value={stueck}
               onChange={(event) => setStueck(event.target.value)} />
        <Input label="Zeitpunkt" type="datetime-local" value={wann}
               max={jetztLokal()}
               onChange={(event) => setWann(event.target.value)} />
        <Input label={`Gebühr in ${order.currency}`}
               inputMode="decimal" value={gebuehrText}
               hint="Leer heißt: die Gebühr des Depots."
               onChange={(event) => setGebuehr(event.target.value)} />
        <Input label="Notiz" value={notiz} placeholder="optional"
               onChange={(event) => setNotiz(event.target.value)} />
      </div>
    </Dialog>
  )
}

/** Eine offene Order stornieren. Mit Grund — wie die Order selbst. */
export function StornoDialog({ order, onFertig, onAbbrechen }: {
  order: Order
} & Rueckmeldung) {
  const [grund, setGrund] = useState('')
  const { laeuft, fehler, starten } = useVorgang(onFertig)

  return (
    <Dialog titel={`Stornieren — ${order.name}`} bestaetigen="Stornieren"
            variant="danger" gesperrt={grund.trim().length < 3}
            laeuft={laeuft} fehler={fehler}
            onBestaetigen={() => starten(() =>
              api.orderCancel(order.id, grund.trim()))}
            onAbbrechen={onAbbrechen}>
      <p className={s.frage}>
        <strong>
          {SIDE[order.side]} {stueckzahl(order.quantity)} Stück,
          Limit {geldtext(order.limit_price)}
        </strong> wird storniert und bindet danach nichts mehr.
      </p>
      <Input label="Grund" value={grund} autoFocus
             placeholder="Kurs davongelaufen, Idee ungültig, …"
             onChange={(event) => setGrund(event.target.value)} />
    </Dialog>
  )
}

/**
 * Eine Ausführung zurücknehmen: Transaktion weg, Order wieder offen.
 *
 * Der Weg für einen Tippfehler beim Buchen — die Transaktion einer Order
 * lässt sich nirgends sonst löschen.
 */
export function ZuruecknehmenDialog({ order, onFertig, onAbbrechen }: {
  order: Order
} & Rueckmeldung) {
  const { laeuft, fehler, starten } = useVorgang(onFertig)
  return (
    <Dialog titel="Ausführung zurücknehmen" bestaetigen="Zurücknehmen"
            variant="danger" laeuft={laeuft} fehler={fehler}
            onBestaetigen={() => starten(() => api.orderRevert(order.id))}
            onAbbrechen={onAbbrechen}>
      <p className={s.frage}>
        Die Transaktion zu <strong>
          {SIDE[order.side]} {stueckzahl(order.quantity)} Stück {order.name}
        </strong> wird gelöscht, die Order ist danach wieder offen. Kasse,
        Position und Status der Idee werden neu gerechnet. Ein Tippfehler
        wird so berichtigt: zurücknehmen und neu buchen.
      </p>
    </Dialog>
  )
}

// ========================================================== Transaktionen

/**
 * Handel ohne Order nachtragen — mit Idee oder ohne.
 *
 * Mit Idee kommen Instrument, Handelsplatz und Währung von ihr. Ohne Idee
 * werden ISIN und Handelsplatz genannt; das ist der Weg für eine Korrektur
 * oder einen Kauf, zu dem es keinen Vorschlag gab.
 */
export function TransaktionDialog({ idee, bestand, depotId, onFertig, onAbbrechen }: {
  idee?: IdeeKurz
  /** Aus dem Bestand heraus: Titel, Platz und Seite stehen fest. */
  bestand?: Bestand
  /** Vorbelegtes Depot, etwa von der Depotseite aus. */
  depotId?: number
} & Rueckmeldung) {
  const wahl = useDepotWahl(depotId)
  const [seite, setSeite] = useState<Seite>(bestand?.seite ?? 'BUY')
  const [isin, setIsin] = useState(bestand?.isin ?? '')
  const [platz, setPlatz] = useState(bestand?.exchange ?? '')
  // Beim Verkauf aus dem Bestand ist die ganze Position vorbelegt — meist
  // verkauft man alles, und weniger tippt sich schneller als mehr.
  const [stueck, setStueck] = useState(
    bestand?.seite === 'SELL' ? String(Number(bestand.quantity)).replace('.', ',') : '')
  const [kurs, setKurs] = useState('')
  const [wann, setWann] = useState(jetztLokal())
  const [tranche, setTranche] = useState('')
  const [gebuehrText, setGebuehr] = useState('')
  const [notiz, setNotiz] = useState('')
  const { laeuft, fehler, starten } = useVorgang(onFertig)

  const waehrung = wahl.depot?.currency ?? 'Depotwährung'
  const titel = idee ?? bestand
  const ort = titel ? `für ${titel.name} an ${titel.exchange}` : ''
  const festgelegt = idee !== undefined || bestand !== undefined
  const vollstaendig = wahl.depotId !== null && positiv(stueck)
                    && positiv(kurs) && wann !== ''
                    && leerOderNichtNegativ(gebuehrText)
                    && (festgelegt
                        || (isin.trim().length === 12 && platz.trim() !== ''))

  function eintragen() {
    const depot = wahl.depotId!
    merkeDepot(depot)
    starten(() => api.transactionAdd(depot, {
      side: seite, quantity: dezimal(stueck), price: dezimal(kurs),
      executed_at: new Date(wann).toISOString(),
      fee: gebuehrText.trim() ? dezimal(gebuehrText) : null,
      note: notiz.trim() || null,
      // Mit Idee bucht der Server auf deren Titel und Platz; sonst auf die
      // ISIN und den Platz — beim Bestand die der Position.
      idea_id: idee?.id ?? bestand?.idea_id ?? null,
      isin: idee || bestand?.idea_id ? null : isin.trim().toUpperCase(),
      exchange: idee || bestand?.idea_id ? null : platz.trim().toUpperCase(),
      // Eine Tranche ist ein Schritt des EINSTIEGS. Ein Verkauf erfüllt
      // keine.
      tranche_sequence: idee && seite === 'BUY' && tranche
        ? Number(tranche) : null,
    }))
  }

  return (
    <Dialog titel={bestand
              ? `${bestand.seite === 'SELL' ? 'Verkauf' : 'Kauf'} — ${bestand.name}`
              : idee ? `Transaktion — ${idee.name}` : 'Transaktion eintragen'}
            bestaetigen="Eintragen"
            gesperrt={!vollstaendig} laeuft={laeuft}
            fehler={fehler ?? wahl.fehler}
            onBestaetigen={eintragen} onAbbrechen={onAbbrechen}>
      <p className={s.hinweis}>
        Was tatsächlich gehandelt wurde, ohne Order{ort ? ` — ${ort}` : ''}.
        Kurs und Gebühr in der Währung des Depots ({waehrung}), nicht in der
        des Handelsplatzes. Eine Transaktion wird nicht geändert, sondern
        gelöscht und neu eingetragen.
      </p>
      {idee?.position.open && (
        <p className={s.lage}>
          Im Markt: {stueckzahl(idee.position.quantity)} Stück
          {idee.position.average_price
            ? ` zu Ø ${geldtext(idee.position.average_price)}` : ''}.
        </p>
      )}
      {bestand && (
        <p className={s.lage}>
          Im Bestand: {stueckzahl(bestand.quantity)} Stück
          {bestand.average_price ? ` zu Ø ${geldtext(bestand.average_price)}` : ''}
          {' '}· {bestand.isin} an {bestand.exchange}.
        </p>
      )}
      <div className={s.felder}>
        <div className={s.breit}><DepotWahl wahl={wahl} /></div>
        {!festgelegt && (
          <>
            <Input label="ISIN" value={isin} autoFocus
                   placeholder="US67066G1040" maxLength={12}
                   onChange={(event) => setIsin(event.target.value)} />
            <Input label="Handelsplatz (MIC)" value={platz}
                   placeholder="XNAS" maxLength={4}
                   hint="Vier Zeichen nach ISO 10383 — XNAS, XNYS, XETR."
                   onChange={(event) => setPlatz(event.target.value)} />
          </>
        )}
        <Select label="Seite" value={seite}
                onChange={(event) => setSeite(event.target.value as Seite)}>
          <option value="BUY">{SIDE.BUY}</option>
          <option value="SELL">{SIDE.SELL}</option>
        </Select>
        <Input label="Stück" inputMode="decimal" value={stueck}
               autoFocus={festgelegt} placeholder="100"
               onChange={(event) => setStueck(event.target.value)} />
        <Input label={`Kurs in ${waehrung}`}
               inputMode="decimal" value={kurs} placeholder="41,80"
               onChange={(event) => setKurs(event.target.value)} />
        <Input label="Zeitpunkt" type="datetime-local" value={wann}
               max={jetztLokal()}
               onChange={(event) => setWann(event.target.value)} />
        {idee && seite === 'BUY' && (
          <TrancheWahl idee={idee} wert={tranche} setzen={setTranche} />
        )}
        <Input label={`Gebühr in ${waehrung}`}
               inputMode="decimal" value={gebuehrText}
               hint="Leer heißt: die Gebühr des Depots."
               onChange={(event) => setGebuehr(event.target.value)} />
        <Input label="Notiz" value={notiz} placeholder="optional"
               onChange={(event) => setNotiz(event.target.value)} />
      </div>
    </Dialog>
  )
}

/** Eine Transaktion ohne Order löschen. Position und Status folgen. */
export function TransaktionLoeschenDialog({ transaktion, onFertig,
                                            onAbbrechen }: {
  transaktion: Transaction
} & Rueckmeldung) {
  const { laeuft, fehler, starten } = useVorgang(onFertig)
  return (
    <Dialog titel="Transaktion löschen" bestaetigen="Löschen" variant="danger"
            laeuft={laeuft} fehler={fehler}
            onBestaetigen={() => starten(() =>
              api.transactionDelete(transaktion.id))}
            onAbbrechen={onAbbrechen}>
      <p className={s.frage}>
        <strong>
          {SIDE[transaktion.side]} {stueckzahl(transaktion.quantity)} Stück{' '}
          {transaktion.name} zu {geldtext(transaktion.price)}
        </strong> vom {zeitpunkt.format(new Date(transaktion.executed_at))} wird
        gelöscht. Kasse, Position und Status werden neu gerechnet. Ein
        Tippfehler wird so berichtigt: löschen und neu eintragen.
      </p>
    </Dialog>
  )
}

// ================================================================== Depot

/** Einlage und Gebühr — die zwei Zahlen, die das Depot hat. */
export function DepotEinstellenDialog({ depot, onFertig, onAbbrechen }: {
  depot: Depot
} & Rueckmeldung) {
  const [name, setName] = useState(depot.name)
  const [einlage, setEinlage] = useState(depot.cash.amount)
  const [gebuehr, setGebuehr] = useState(depot.fee_per_order.amount)
  const { laeuft, fehler, starten } = useVorgang(onFertig)

  return (
    <Dialog titel="Depot einstellen" bestaetigen="Speichern"
            gesperrt={name.trim() === '' || !nichtNegativ(einlage)
                      || !nichtNegativ(gebuehr)}
            laeuft={laeuft} fehler={fehler}
            onBestaetigen={() => starten(() =>
              api.depotConfigure(depot.id, {
                name: name.trim(), cash: dezimal(einlage),
                fee_per_order: dezimal(gebuehr),
              }))}
            onAbbrechen={onAbbrechen}>
      <p className={s.hinweis}>
        Die Einlage ist das Kapital, das grundsätzlich da ist — nicht der
        Kontostand. Der ergibt sich aus Einlage, Käufen und Verkäufen. Die
        Gebühr wird bei jeder Ausführung vorbelegt und in der Reservierung
        offener Kauforders mitgerechnet. Die Währung ({depot.currency})
        steht fest.
      </p>
      <div className={s.felder}>
        <div className={s.breit}>
          <Input label="Name" value={name} autoFocus
                 onChange={(event) => setName(event.target.value)} />
        </div>
        <Input label={`Einlage in ${depot.currency}`} inputMode="decimal"
               value={einlage}
               onChange={(event) => setEinlage(event.target.value)} />
        <Input label={`Gebühr je Order in ${depot.currency}`}
               inputMode="decimal" value={gebuehr}
               onChange={(event) => setGebuehr(event.target.value)} />
      </div>
    </Dialog>
  )
}

/** Ein Depot anlegen. Die Währung steht danach fest. */
export function DepotAnlegenDialog({ onFertig, onAbbrechen }: Rueckmeldung) {
  const [name, setName] = useState('')
  const [waehrung, setWaehrung] = useState('EUR')
  const [einlage, setEinlage] = useState('')
  const [gebuehr, setGebuehr] = useState('1')
  const { laeuft, fehler, starten } = useVorgang(onFertig)

  const vollstaendig = name.trim() !== '' && /^[A-Za-z]{3}$/.test(waehrung.trim())
                    && nichtNegativ(einlage) && nichtNegativ(gebuehr)

  return (
    <Dialog titel="Depot anlegen" bestaetigen="Anlegen"
            gesperrt={!vollstaendig} laeuft={laeuft} fehler={fehler}
            onBestaetigen={() => starten(() =>
              api.depotCreate({
                name: name.trim(), currency: waehrung.trim().toUpperCase(),
                cash: dezimal(einlage), fee_per_order: dezimal(gebuehr),
              }))}
            onAbbrechen={onAbbrechen}>
      <p className={s.hinweis}>
        Ein Depot je Konto oder Strategie. Jede Order und jede Transaktion
        gehört zu genau einem. Die Währung lässt sich später nicht ändern —
        an ihr hängt jede Kassenbewegung.
      </p>
      <div className={s.felder}>
        <div className={s.breit}>
          <Input label="Name" value={name} autoFocus
                 placeholder="Echtgeld, Spielgeld, Dividenden …"
                 onChange={(event) => setName(event.target.value)} />
        </div>
        <Input label="Währung" value={waehrung} maxLength={3}
               hint="ISO 4217 — EUR, USD, CHF."
               onChange={(event) => setWaehrung(event.target.value)} />
        <Input label="Einlage" inputMode="decimal" value={einlage}
               placeholder="50000"
               onChange={(event) => setEinlage(event.target.value)} />
        <Input label="Gebühr je Order" inputMode="decimal" value={gebuehr}
               onChange={(event) => setGebuehr(event.target.value)} />
      </div>
    </Dialog>
  )
}

/** Ein leeres Depot löschen. Mit Orders oder Transaktionen weist es der
 *  Server ab — Handelsgeschichte wird nicht gelöscht. */
export function DepotLoeschenDialog({ depot, onFertig, onAbbrechen }: {
  depot: Depot
} & Rueckmeldung) {
  const { laeuft, fehler, starten } = useVorgang(onFertig)
  return (
    <Dialog titel="Depot löschen" bestaetigen="Löschen" variant="danger"
            laeuft={laeuft} fehler={fehler}
            onBestaetigen={() => starten(() => api.depotDelete(depot.id))}
            onAbbrechen={onAbbrechen}>
      <p className={s.frage}>
        <strong>{depot.name}</strong> wird gelöscht. Das geht nur, solange
        es weder Orders noch Transaktionen hat.
      </p>
    </Dialog>
  )
}
